import numeral from "numeral";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { useAsync } from "react-use";
import { useMessage } from "@/contexts";
import {
  TransferCategory,
  TransferCreate,
  TransferService,
} from "@/domains/transfer";
import { UserService } from "@/domains/user";
// components
import { Button, Checkbox, Form, Input, Spin } from "antd";

export const TenantManageCreateMemberWithdrawPage = () => {
  const navigate = useNavigate();

  const message = useMessage();

  const [form] = Form.useForm<TransferCreate>();

  const [loading, setLoading] = useState(false);

  const [isSyncToAmount, setIsSyncToAmount] = useState(true);

  const [isSyncToAmount0, setIsSyncToAmount0] = useState(false);

  const withdraw_rate = useAsync(async () => {
    const res = await UserService.getMyRates().run();
    return res.data.msg.withdraw_rate;
  });

  const withdraw_round = useAsync(async () => {
    const res = await UserService.getMyRounds().run();
    return res.data.msg.withdraw_round;
  });

  const isWithdrawRateRequired =
    withdraw_rate.value !== undefined && parseFloat(withdraw_rate.value) !== 1;

  const handleAmount0Input = useCallback(
    (value: string) => {
      if (!isSyncToAmount) return;

      if (Number.isNaN(parseFloat(value))) {
        form.setFieldValue("amount", "");
        return;
      }

      const amount = numeral(value).divide(withdraw_rate.value).value();
      if (amount === null) {
        return;
      }

      switch (withdraw_round.value) {
        case 10:
          form.setFieldValue("amount", Math.ceil(amount * 1) / 1);
          break;
        case 11:
          form.setFieldValue("amount", Math.ceil(amount * 10) / 10);
          break;
        case 12:
          form.setFieldValue("amount", Math.ceil(amount * 100) / 100);
          break;
        case 50:
          form.setFieldValue("amount", amount.toFixed(0));
          break;
        case 51:
          form.setFieldValue("amount", amount.toFixed(1));
          break;
        case 52:
          form.setFieldValue("amount", amount.toFixed(2));
          break;
        case 90:
          form.setFieldValue("amount", Math.floor(amount * 1) / 1);
          break;
        case 91:
          form.setFieldValue("amount", Math.floor(amount * 10) / 10);
          break;
        case 92:
          form.setFieldValue("amount", Math.floor(amount * 100) / 100);
          break;
      }
    },
    [form, isSyncToAmount, withdraw_rate.value, withdraw_round.value]
  );

  const handleAmountInput = useCallback(
    (value: string) => {
      if (!isSyncToAmount0 || !isWithdrawRateRequired) return;

      if (Number.isNaN(parseFloat(value))) {
        form.setFieldValue("amount0", "");
        return;
      }

      const amount0 = numeral(value).multiply(withdraw_rate.value).value();
      if (amount0 === null) return;

      form.setFieldValue("amount0", Math.ceil(amount0));
    },
    [form, isSyncToAmount0, isWithdrawRateRequired, withdraw_rate.value]
  );

  const handleFinish = async (values: TransferCreate) => {
    const data = {
      ...values,
      currency: "USDT",
    };

    try {
      setLoading(true);
      await TransferService.create({
        ...data,
        category: TransferCategory.MemberWithdraw,
        currency: "USDT",
      }).run();
      void message.messageApi.success("申請成功!");
      navigate("/tenant/reports/member-withdraw");
    } catch (e) {
      if (isAxiosError(e)) {
        void message.messageApi.error(e.response?.data.msg as string);
      }
      const res = await UserService.getMyRates().run();
      form.setFieldValue("rate", res.data.msg.withdraw_rate);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const field = form.getFieldInstance("amount0");
    if (!field) return;
    const input = form.getFieldInstance("amount0").input as HTMLInputElement;
    handleAmount0Input(input.value);
  }, [form, handleAmount0Input]);

  useEffect(() => {
    const field = form.getFieldInstance("amount");
    if (!field) return;
    const input = form.getFieldInstance("amount").input as HTMLInputElement;
    handleAmountInput(input.value);
  }, [form, handleAmountInput]);

  const Amount0FormItem = () => (
    <Form.Item
      label="提單金額"
      rules={[{ required: true, message: "提單金額不可空白" }]}
      name="amount0"
    >
      <Input
        type="number"
        placeholder="請輸入提單金額"
        onInput={(e) =>
          handleAmount0Input((e.target as HTMLInputElement).value)
        }
        suffix={
          <Checkbox
            checked={isSyncToAmount}
            onChange={(e) => setIsSyncToAmount(e.target.checked)}
          >
            自動計算至提單顆數
          </Checkbox>
        }
      />
    </Form.Item>
  );

  const RateFormItem = () => (
    <Form.Item label="提單匯率" name="rate" initialValue={withdraw_rate.value}>
      <Input readOnly type="number" />
    </Form.Item>
  );

  return (
    <div className="w:500 p:16 bg:white r:4 mt:0 mb:0 ml:auto mr:auto">
      <h4 className="mb:24 t:center fg:secondary">申請會員代付</h4>

      <Spin spinning={withdraw_rate.loading}>
        <Form
          form={form}
          labelCol={{ span: 5 }}
          initialValues={{ rate: withdraw_rate.value }}
          onFinish={handleFinish}
        >
          {isWithdrawRateRequired && <Amount0FormItem />}
          {isWithdrawRateRequired && <RateFormItem />}

          <Form.Item
            label="提單顆數"
            rules={[{ required: true }]}
            name="amount"
          >
            <Input
              type="number"
              placeholder="請輸入提單顆數"
              onInput={(e) =>
                handleAmountInput((e.target as HTMLInputElement).value)
              }
              suffix={
                isWithdrawRateRequired && (
                  <Checkbox
                    checked={isSyncToAmount0}
                    onChange={(e) => setIsSyncToAmount0(e.target.checked)}
                  >
                    自動計算至提單金額
                  </Checkbox>
                )
              }
            />
          </Form.Item>

          <Form.Item label="平台單號" name={["order", "id"]}>
            <Input placeholder="請輸入平台單號" />
          </Form.Item>

          <Form.Item label="會員代碼" name={["member", "id"]}>
            <Input placeholder="請輸入會員代碼" />
          </Form.Item>

          <Form.Item label="會員帳號" name={["member", "username"]}>
            <Input placeholder="請輸入會員帳號" />
          </Form.Item>

          <Form.Item label="會員名稱" name={["member", "name"]}>
            <Input placeholder="請輸入會員名稱" />
          </Form.Item>

          <Form.Item
            label="收款錢包"
            name={["target", "address"]}
            rules={[{ required: true, message: "收款錢包不可空白" }]}
          >
            <Input placeholder="請輸入收款錢包" />
          </Form.Item>

          <Form.Item label="備註" name="note">
            <Input placeholder="請輸入備註" />
          </Form.Item>

          <div className="flex jc:center gap:16">
            <Button type="primary" htmlType="submit" loading={loading}>
              送出
            </Button>

            <Button htmlType="reset">清除</Button>
          </div>
        </Form>
      </Spin>
    </div>
  );
};
